import { useAuthorization } from '@shared/auth/use-authorization';
import { hasRole, Role } from '@shared/auth';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HiArchiveBoxArrowDown, HiArrowUturnUp, HiMagnifyingGlass, HiOutlineArchiveBox, HiOutlineClipboardDocumentList, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import { useAuth } from 'react-oidc-context';

import { Timestamp } from '@/components/date-time';
import { Button } from '@/components-new/button';
import { ConfirmDeleteDialog } from '@/components-new/confirm-delete-dialog';
import { DataPaginator } from '@/components-new/data-paginator';
import { DataTable, useDataTable } from '@/components-new/data-table';
import { EmptyState, EmptyStateBody, EmptyStateHeading } from '@/components-new/empty-state';
import { Field, FieldGroup } from '@/components-new/fieldset';
import { Input, InputGroup } from '@/components-new/input';
import { Loader } from '@/components-new/loader';
import { Overlay } from '@/components-new/overlay';
import { Page } from '@/components-new/page';
import { PageTitleRow } from '@/components-new/page-title-row';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@/components-new/tab';
import { TextLink } from '@/components-new/text';
import { useMarketBaskets, MarketBasket } from '@/features/market-baskets';
import { useMarketBasketService } from '@/features/market-baskets/api/use-market-baskets-service';
import { notifyError, notifySuccess } from '@/lib/notification/notifications';
import { Problem } from '@shared/errors/error-types';
import { useDebounceValue } from '@/hooks/use-debounce-value';
import { useUnmount } from '@/hooks/use-unmount';
import MarketBasketPurposeBadge from '@/features/market-baskets/components/market-basket-purpose-badge';

const columnHelper = createColumnHelper<MarketBasket>();

type FilterForm = {
  name?: string;
  client?: string;
  purpose?: string;
};

const MarketBasketsPage = () => {
  const { marketBaskets, loadMarketBaskets, loadingMarketBaskets } = useMarketBaskets();
  const { archiveMarketBasket, deleteMarketBasket } = useMarketBasketService();
  const { checkPolicies } = useAuthorization();
  const { user } = useAuth();

  const canManageMarketBaskets = checkPolicies(['canManageMarketBaskets']);
  // @ts-expect-error TS(2345): hasRole expects User, user may be null/undefined
  const isAdmin = hasRole(user, Role.Administrator);

  const filtersWatchSubscriptionRef = React.useRef<{ unsubscribe: () => void } | undefined>();
  const filtersForm = useForm<FilterForm>();
  const [filters, setFilters] = useDebounceValue(filtersForm.getValues(), 350);
  const { watch } = filtersForm;

  const [deletingMarketBasket, setDeletingMarketBasket] = React.useState<MarketBasket | undefined>();

  React.useEffect(() => {
    filtersWatchSubscriptionRef.current = watch(setFilters);
  }, [setFilters, watch]);

  useUnmount(() => {
    filtersWatchSubscriptionRef.current?.unsubscribe();
  });

  React.useEffect(() => {
    loadMarketBaskets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredMarketBaskets = useMemo(() => {
    const { name, client, purpose } = filters;
    return marketBaskets.filter(mb =>
      (!name || mb.name.toLowerCase().includes(name.toLowerCase()))
      && (!client || mb.client.label.toLowerCase().includes(client.toLowerCase()))
      && (!purpose || mb.purpose.toLowerCase().includes(purpose.toLowerCase()))
    );
  }, [filters, marketBaskets]);

  const activeMarketBaskets = useMemo(
    () => filteredMarketBaskets.filter(mb => !mb.archivedAt),
    [filteredMarketBaskets]
  );

  const archivedMarketBaskets = useMemo(
    () => filteredMarketBaskets.filter(mb => mb.archivedAt),
    [filteredMarketBaskets]
  );

  const handleArchive = (id: number, name: string) => {
    archiveMarketBasket(id)
      .then(() => {
        loadMarketBaskets();
        notifySuccess({ title: 'Success', message: `Archived market basket ${name}` });
      })
      .catch(() => {
        notifyError({ title: 'Error', message: `Unable to archive market basket ${name}. Please contact your administrator.` });
      });
  };

  const handleUnArchive = (id: number, name: string) => {
    archiveMarketBasket(id)
      .then(() => {
        loadMarketBaskets();
        notifySuccess({ title: 'Success', message: `Re-Activated market basket ${name}` });
      })
      .catch(() => {
        notifyError({ title: 'Error', message: `Unable to re-activate market basket ${name}. Please contact your administrator.` });
      });
  };

  const handleDelete = (marketBasket: MarketBasket) => {
    deleteMarketBasket(marketBasket.id)
      .then(() => {
        loadMarketBaskets();
        notifySuccess({ title: 'Success', message: `Deleted market basket ${marketBasket.name}` });
      })
      .catch((httpError) => {
        httpError.response.json().then((problem: Problem) => {
          notifyError({ title: 'Error', message: `Unable to delete market basket ${marketBasket.name}. ${problem.detail}` });
        });
      })
      .finally(() => setDeletingMarketBasket(undefined));
  };

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: ({ row }) => (
        <TextLink href={`/market-baskets/${row.original.id}`}>
          {row.original.name}
        </TextLink>
      )
    }),
    columnHelper.accessor('client', {
      id: 'client',
      header: 'Client',
      cell: ({ row }) => row.original.client.label
    }),
    columnHelper.accessor('purpose', {
      id: 'purpose',
      header: 'Purpose',
      cell: ({ row }) => <MarketBasketPurposeBadge purpose={row.original.purpose} />
    }),
    columnHelper.accessor('lastRefreshedAt', {
      header: 'Last Refreshed At',
      cell: ({ row }) => <Timestamp value={row.original.lastRefreshedAt}/>
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {canManageMarketBaskets && !row.original.archivedAt && (
            <Button
              plain
              title="Archive Market Basket"
              aria-label="Archive Market Basket"
              onClick={() => handleArchive(row.original.id, row.original.name)}
            >
              <HiArchiveBoxArrowDown/>
            </Button>
          )}
          {canManageMarketBaskets && row.original.archivedAt && (
            <Button
              plain
              title="Re-Activate Market Basket"
              aria-label="Re-Activate Market Basket"
              onClick={() => handleUnArchive(row.original.id, row.original.name)}
            >
              <HiArrowUturnUp/>
            </Button>
          )}
          {isAdmin && (
            <Button
              plain
              title="Delete Market Basket"
              aria-label="Delete Market Basket"
              onClick={() => setDeletingMarketBasket(row.original)}
            >
              <HiOutlineTrash/>
            </Button>
          )}
        </div>
      )
    })
  ], [canManageMarketBaskets, isAdmin]);

  const activeTable = useDataTable({
    data: activeMarketBaskets,
    columns,
    enableSorting: true,
    enablePagination: true,
  });

  const archivedTable = useDataTable({
    data: archivedMarketBaskets,
    columns,
    enableSorting: true,
    enablePagination: true,
  });

  const showLoader = loadingMarketBaskets;
  const hasActiveMarketBaskets = activeMarketBaskets.length > 0;
  const hasArchivedMarketBaskets = archivedMarketBaskets.length > 0;

  return (
    <Page
      title="Market Baskets"
      header={({ title }) => (
        <PageTitleRow title={title}>
          <Button
            aria-label="Add Market Basket"
            href="/market-baskets/new"
          >
            <HiOutlinePlus/>
            Add New
          </Button>
        </PageTitleRow>
      )}
    >
      <div className="my-4">
        <FieldGroup className="max-w-4xl">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Controller
              control={filtersForm.control}
              defaultValue=""
              name="name"
              render={({ field }) => (
                <Field>
                  <InputGroup>
                    <HiMagnifyingGlass/>
                    <Input {...field} type="search" placeholder="Search by Name"/>
                  </InputGroup>
                </Field>
              )}
            />
            <Controller
              control={filtersForm.control}
              defaultValue=""
              name="client"
              render={({ field }) => (
                <Field>
                  <InputGroup>
                    <HiMagnifyingGlass/>
                    <Input {...field} type="search" placeholder="Search by Client"/>
                  </InputGroup>
                </Field>
              )}
            />
            <Controller
              control={filtersForm.control}
              defaultValue=""
              name="purpose"
              render={({ field }) => (
                <Field>
                  <InputGroup>
                    <HiMagnifyingGlass/>
                    <Input {...field} type="search" placeholder="Search by Purpose"/>
                  </InputGroup>
                </Field>
              )}
            />
          </div>
        </FieldGroup>
      </div>

      <TabGroup>
        <TabList>
          <Tab><HiOutlineClipboardDocumentList className="size-4"/>Active</Tab>
          <Tab><HiOutlineArchiveBox className="size-4"/>Archived</Tab>
        </TabList>
        <TabPanels>
          <TabPanel className="pt-4">
            <DataTable table={activeTable}/>
            {!showLoader && !hasActiveMarketBaskets && (
              <EmptyState>
                <HiOutlineClipboardDocumentList className="size-12 text-gray-500"/>
                <EmptyStateHeading>No market baskets</EmptyStateHeading>
                <EmptyStateBody>No active market baskets match your search.</EmptyStateBody>
              </EmptyState>
            )}
            {showLoader && (
              <div className="relative flex h-48 items-center justify-center">
                <Overlay>
                  <Loader message="Loading market baskets..."/>
                </Overlay>
              </div>
            )}
            {!showLoader && hasActiveMarketBaskets && <DataPaginator className="pt-2" table={activeTable}/>}
          </TabPanel>
          <TabPanel className="pt-4">
            <DataTable table={archivedTable}/>
            {!showLoader && !hasArchivedMarketBaskets && (
              <EmptyState>
                <HiOutlineArchiveBox className="size-12 text-gray-500"/>
                <EmptyStateHeading>No archived market baskets</EmptyStateHeading>
                <EmptyStateBody>No archived market baskets match your search.</EmptyStateBody>
              </EmptyState>
            )}
            {showLoader && (
              <div className="relative flex h-48 items-center justify-center">
                <Overlay>
                  <Loader message="Loading market baskets..."/>
                </Overlay>
              </div>
            )}
            {!showLoader && hasArchivedMarketBaskets && <DataPaginator className="pt-2" table={archivedTable}/>}
          </TabPanel>
        </TabPanels>
      </TabGroup>

      <ConfirmDeleteDialog
        item={deletingMarketBasket}
        title="Delete Market Basket"
        message={`Are you sure you want to delete ${deletingMarketBasket?.name}?`}
        deleteButtonTitle="Delete Market Basket"
        isDeleting={false}
        onClose={() => setDeletingMarketBasket(undefined)}
        onDeleteConfirmed={handleDelete}
      />
    </Page>
  );
};

export default MarketBasketsPage;
